import React, { useState, useEffect } from "react";
import { Form, Input, Button, Radio, Space, Spin, Progress, Modal } from "antd";
import {
  CheckCircleOutlined,
  PhoneFilled,
  LoadingOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  _getBVNInstructions,
  _submitBVN,
  _getBVNStatus,
  _getKycRequirements,
  _callKycWebhookSimulation,
} from "../../common/axios_services";
import TopNav from "../../components/top-nav";
import CustomDatePicker from "../../components/CustomDatePicker";
import AppRoutes from "../../common/routes";
import "../../assets/css/dashboard.css";

import useKycSocket from "../../hooks/useKycSocket";
import { useSelector } from "react-redux";

const BVNVerification = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [ussdInstructions, setUssdInstructions] = useState(null);
  const [verificationStep, setVerificationStep] = useState("instructions"); // 'instructions' | 'form' | 'verifying' | 'timeout'
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [currentVerificationStep, setCurrentVerificationStep] = useState(0);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [timeoutModalVisible, setTimeoutModalVisible] = useState(false);
  const [verificationSteps, setVerificationSteps] = useState([]);
  const [kycRequirements, setKycRequirements] = useState(null);

  const userData = useSelector((state) => state.userProfile.dataFetched);
  const isDev = process.env.REACT_APP_NODE_ENV === "dev";

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 18, color: "#fff" }} spin />
  );
  const newAntIcon = <LoadingOutlined style={{ fontSize: 40 }} spin />;

  useEffect(() => {
    fetchUssdInstructions();
    fetchKycRequirements();
  }, []);

  useEffect(() => {
    let timeoutId;

    if (verificationStep === "verifying") {
      setVerificationProgress(0);
      setCurrentVerificationStep(0);
      setVerificationMessage("Initializing verification...");
      setVerificationSteps([]);

      timeoutId = setTimeout(
        () => {
          setTimeoutModalVisible(true);
          setVerificationStep("timeout");
        },
        5 * 60 * 1000,
      );
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [verificationStep]);

  const handleProgressUpdate = (data) => {
    if (verificationStep === "verifying") {
      setVerificationProgress(data.progress || 0);
      setCurrentVerificationStep(data.step || 0);
      setVerificationMessage(data.message || "Processing...");

      if (data.stepLabel || data.stepIcon) {
        setVerificationSteps((prev) => {
          const newSteps = [...prev];
          const stepIndex = data.step || 0;
          newSteps[stepIndex] = {
            key: `step-${stepIndex}`,
            label: data.stepLabel || `Step ${stepIndex + 1}`,
            icon: data.stepIcon || "⏳",
          };
          return newSteps;
        });
      }
    }
  };

  const handleVerificationComplete = (data) => {
    setVerificationStep("completed");

    if (data.status === "successful") {
      Modal.success({
        title: (
          <div className="flex items-center gap-3 text-green-700">
            <span className="font-bold text-2xl">
              {data.title || "Good news, your account has been upgraded to Tier 2."}
            </span>
          </div>
        ),
        content: (
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-gray-800 font-medium ">
                {data.message || "Good news, your account has been upgraded to Tier 2."}
              </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-gray-800 font-medium mb-3 ">
                Your new wallet details are:
              </p>
              <div className=" space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Bank:</span>
                  <span className="font-semibold text-gray-800">
                    {data.bankName || ""}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">
                    Account Number:
                  </span>
                  <span className="font-semibold text-gray-800">
                    {data.nuban || ""}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-gray-800 font-medium ">
                {data.instruction || "Use your NUBAN account to fund your Wallet"}
              </p>
            </div>
          </div>
        ),
        onOk: () => navigate(AppRoutes.home),
        centered: true,
        okButtonProps: {
          className:
            "bg-black border-none text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300",
        },
      });
    } else if (data.status === "rejected") {
      Modal.error({
        title: (
          <div className="flex items-center gap-3 text-red-700">
            <CheckCircleOutlined className="text-2xl text-red-500" />
            <span className="font-bold text-[13px]">
              {data.title || "KYC Verification Rejected"}
            </span>
          </div>
        ),
        content: (
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-gray-800 font-medium">
                {data.message || "Your KYC verification was not approved."}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-[13px] text-gray-700 font-medium">
                {data.instruction ||
                  "BEFORE YOU RETRY, CHECK YOUR BVN DETAILS FROM YOUR REGISTERED MOBILE NUMBER - Check BVN: dial *565*0# or *565*1#"}
              </p>
            </div>
          </div>
        ),
        centered: true,
        okButtonProps: {
          className:
            "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 border-none text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300",
        },
      });
      setVerificationStep("form");
    } else if (data.status === "error") {
      Modal.warning({
        title: (
          <div className="flex items-center gap-3 text-orange-700">
            <CheckCircleOutlined className="text-2xl text-orange-500" />
            <span className="font-bold text-[13px]">
              {data.title || "KYC Verification Error"}
            </span>
          </div>
        ),
        content: (
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-gray-800 font-medium">
                {data.message || "Your KYC verification was not approved."}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-[13px] text-gray-700 font-medium">
                {data.instruction ||
                  "This may be due to a minor error, please retry in an hour."}
              </p>
            </div>
          </div>
        ),
        centered: true,
        okButtonProps: {
          className:
            "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border-none text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300",
        },
      });
      setVerificationStep("form");
    }
  };

  useKycSocket(handleProgressUpdate, handleVerificationComplete);

  const fetchUssdInstructions = async () => {
    try {
      setLoading(true);
      const response = await _getBVNInstructions();
      setUssdInstructions({
        bvnUssd: response.data?.data?.displayBvn?.code,
        nameUssd: response.data?.data?.displayFullName?.code,
        instructions: response.data?.data?.instructions?.join(" "),
      });
    } catch (error) {
      toast.error("Failed to load BVN instructions");
    } finally {
      setLoading(false);
    }
  };

  const fetchKycRequirements = async () => {
    try {
      const response = await _getKycRequirements();
      const reqData = response?.data?.data || response?.data;
      if (reqData && typeof reqData === "object") {
        setKycRequirements(reqData);
      }
    } catch (error) {
      console.warn("Failed to load KYC requirements", error);
    }
  };

  const handleVerifyBVN = async (values) => {
    if (kycRequirements && kycRequirements.hasEnoughBalance === false) {
      toast.error(
        `Insufficient wallet balance. You need at least ₦${kycRequirements.requiredBalance?.toLocaleString()} to upgrade to Tier 2.`,
      );
      return;
    }

    try {
      setLoading(true);
      setVerificationStep("verifying");

      const fullName = [values.firstName, values.middleName, values.lastName]
        .map((s) => s?.trim())
        .filter(Boolean)
        .join(" ");

      const payload = {
        bvn: values.bvn?.trim(),
        firstName: values.firstName?.trim(),
        middleName: values.middleName?.trim() || undefined,
        lastName: values.lastName?.trim(),
        fullName,
        dateOfBirth: values.dateOfBirth,
        gender: values.gender,
      };

      const res = await _submitBVN(payload);
      const resData = res?.data?.data || res?.data;

      if (resData?.bvnVerified) {
        handleVerificationComplete({
          status: "successful",
          bankName: resData.bankName,
          nuban: resData.nuban,
          title: "Good news, your account has been upgraded to Tier 2.",
          message: `Good news, your account has been upgraded to Tier 2. Your new wallet details are: ${resData.bankName} & ${resData.nuban}. Use your NUBAN account to fund your Wallet`,
          instruction: "Use your NUBAN account to fund your Wallet",
        });
        return;
      }

      const anchorCustomerId = resData?.anchorCustomerId;

      if (isDev) {
        setTimeout(async () => {
          try {
            const webhookPayload = {
              event: "approved",
              customerId: anchorCustomerId || "test_customer",
              rejectionReasons: [],
            };

            console.log(
              "🔧 Simulating KYC webhook in development:",
              webhookPayload,
            );

            await _callKycWebhookSimulation(webhookPayload);
            console.log("✅ KYC webhook simulation triggered");
          } catch (error) {
            console.error("Failed to simulate KYC webhook:", error);
          }
        }, 3000);
      }
    } catch (error) {
      setVerificationStep("form");
      toast.error(
        error.response?.data?.message ||
        "Failed to submit verification request. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderInstructions = () => (
    <div className="contain p-4 md:p-8 bg-gradient-to-br from-blue-50 via-white to-indigo-50 min-h-screen">
      <div className=" mb-12 transition-all duration-700 ease-in-out">
        <div className="mb-8 inline-block p-6 bg-black rounded-full shadow-xl transform hover:scale-105 transition-transform duration-300">
          <PhoneFilled className="text-4xl text-white" />
        </div>
        <h3 className="sec-page-title text-2xl md:text-3xl font-bold text-gray-900 mb-6 tracking-tight">
          BVN Verification Required
        </h3>
        <p className="text-gray-700 text-2xl md:text-xl leading-relaxed max-w-2xl mx-auto">
          To upgrade to Tier 2, we need to verify your identity using your BVN.
        </p>
      </div>

      {ussdInstructions && (
        <div className="space-y-6 mb-8">
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-md">
            <h4 className="text-gray-900 font-semibold mb-4 text-2xl">
              Step 1: Get Your BVN & Name Details
            </h4>
            <div className="text-2xl">
              <p className="mb-3 text-gray-700">
                Dial{" "}
                <strong className="text-black">
                  {ussdInstructions.bvnUssd}
                </strong>{" "}
                to get your BVN
              </p>
              <p className="mb-3 text-gray-700">
                Dial{" "}
                <strong className="text-black">
                  {ussdInstructions.nameUssd}
                </strong>{" "}
                to get your registered name
              </p>
              <p className="text-gray-600">
                You will receive both via SMS from your mobile network provider
              </p>
            </div>
          </div>

          <div className="bg-white  p-6 rounded-lg shadow-md border border-gray-200">
            <p className="text-gray-600 text-[13px] leading-relaxed">
              {ussdInstructions.instructions}
            </p>
          </div>
        </div>
      )}

      {kycRequirements && kycRequirements.hasEnoughBalance === false && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-8 text-left max-w-2xl mx-auto shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">⚠️</span>
            <h4 className="text-red-800 font-bold text-xl">
              Wallet Funding Required
            </h4>
          </div>
          <p className="text-gray-700 text-[13px] mb-4">
            A minimum balance of ₦{Number(kycRequirements.minBalance || 0).toLocaleString()} plus an upgrade fee of ₦{Number(kycRequirements.upgradeFee || 0).toLocaleString()} (total ₦{Number(kycRequirements.requiredBalance || 0).toLocaleString()}) is required to upgrade to Tier 2.
          </p>
          <div className="bg-white p-4 rounded-xl border border-red-100 mb-5 space-y-2 text-base">
            <div className="flex text-[13px] justify-between text-gray-600">
              <span>Required Balance:</span>
              <span className="font-semibold text-gray-900">
                ₦{Number(kycRequirements.requiredBalance || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex text-[13px] justify-between text-gray-600">
              <span>Your Spendable Balance:</span>
              <span className="font-semibold text-gray-900">
                ₦{Number(kycRequirements.spendableBalance || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex text-[13px] justify-between text-red-600 font-bold border-t border-gray-100 pt-2">
              <span>Shortfall:</span>
              <span>₦{Number(kycRequirements.shortfall || 0).toLocaleString()}</span>
            </div>
          </div>
          <button
            type="button"
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 px-6 rounded-xl transition-colors shadow-sm cursor-pointer border-0"
            onClick={() =>
              navigate(AppRoutes.wallet || "/wallet", {
                state: { openFundWallet: true },
              })
            }
          >
            Fund Wallet Now
          </button>
        </div>
      )}

      <div className="welcome-buttons mt-8">
        <div className="welcome-buttons-content">
          {!loading ? (
            <button
              className={`btn-primary bg-black text-white px-10 py-4 rounded-xl font-semibold transition-colors ${kycRequirements && kycRequirements.hasEnoughBalance === false
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-800 cursor-pointer"
                }`}
              onClick={() => {
                if (kycRequirements && kycRequirements.hasEnoughBalance === false) {
                  toast.error(
                    `Please fund your wallet with at least ₦${Number(kycRequirements.shortfall || 0).toLocaleString()} before proceeding.`,
                  );
                  return;
                }
                setVerificationStep("form");
              }}
              disabled={kycRequirements && kycRequirements.hasEnoughBalance === false}
              aria-label="Proceed to enter BVN details"
            >
              I Have My BVN & Name Details
            </button>
          ) : (
            <button
              className="btn-primary bg-black text-white px-8 py-3 rounded-lg font-semibold opacity-60"
              disabled
            >
              <Spin indicator={antIcon} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderForm = () => (
    <div className="contain p-4 md:p-8 bg-gradient-to-br from-blue-50 via-white to-indigo-50 min-h-screen">
      <div className=" mb-12 transition-all duration-700 ease-in-out">
        <h3 className="sec-page-title text-2xl md:text-3xl font-bold text-gray-900 mb-6 tracking-tight">
          Enter BVN Details
        </h3>
        <p className="text-gray-700 text-2xl md:text-xl leading-relaxed max-w-2xl mx-auto">
          Please provide your BVN information for verification
        </p>
      </div>

      <div className="bg-white rounded-2xl p-6 md:p-10 shadow-2xl border border-gray-100 max-w-2xl mx-auto transform hover:shadow-3xl transition-shadow duration-300">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleVerifyBVN}
          autoComplete="off"
        >
          <div className="form_group mb-6">
            <label className="block text-gray-900 font-semibold mb-2">
              Date of Birth
            </label>
            <Form.Item
              name="dateOfBirth"
              rules={[
                { required: true, message: "Please select your date of birth" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const year = value.split("-")[0];
                    if (year.length !== 4) {
                      return Promise.reject("Year must be 4 digits (e.g. 1990)");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <CustomDatePicker
                placeholder="Date of Birth"
                className="h-14"
              />
            </Form.Item>
          </div>

          <div className="form_group mb-6">
            <label className="block text-gray-900 font-semibold mb-2">
              Gender
            </label>
            <Form.Item
              name="gender"
              rules={[{ required: true, message: "Please select your gender" }]}
            >
              <Radio.Group className="flex gap-4">
                <Radio value="MALE" className="text-xl">
                  Male
                </Radio>
                <Radio value="FEMALE" className="text-xl">
                  Female
                </Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="form_group mb-6">
            <label className="block text-gray-900 font-semibold mb-2">
              First Name (as registered with BVN)
            </label>
            <Form.Item
              name="firstName"
              rules={[
                {
                  required: true,
                  message: "Please enter your first name as registered with BVN",
                },
              ]}
            >
              <Input
                placeholder="Enter first name"
                className="h-14 rounded-lg border-gray-300"
              />
            </Form.Item>
          </div>

          <div className="form_group mb-6">
            <label className="block text-gray-900 font-semibold mb-2">
              Middle Name (optional, as registered with BVN)
            </label>
            <Form.Item name="middleName">
              <Input
                placeholder="Enter middle name (optional)"
                className="h-14 rounded-lg border-gray-300"
              />
            </Form.Item>
          </div>

          <div className="form_group mb-6">
            <label className="block text-gray-900 font-semibold mb-2">
              Last Name (as registered with BVN)
            </label>
            <Form.Item
              name="lastName"
              rules={[
                {
                  required: true,
                  message: "Please enter your last name as registered with BVN",
                },
              ]}
            >
              <Input
                placeholder="Enter last name (surname)"
                className="h-14 rounded-lg border-gray-300"
              />
            </Form.Item>
          </div>

          <div className="form_group mb-8">
            <label className="block text-gray-900 font-semibold mb-2">
              BVN
            </label>
            <Form.Item
              name="bvn"
              rules={[
                { required: true, message: "Please enter your BVN" },
                { pattern: /^\d{11}$/, message: "BVN must be 11 digits" },
              ]}
            >
              <Input
                placeholder="Enter 11-digit BVN"
                maxLength={11}
                type="number"
                inputMode="numeric"
                className="h-14 rounded-lg border-gray-300"
              />
            </Form.Item>
          </div>

          {kycRequirements && kycRequirements.hasEnoughBalance === false && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-700 font-medium mb-3 text-[13px]">
                Insufficient balance to upgrade. You need at least ₦{Number(kycRequirements.requiredBalance || 0).toLocaleString()} (current balance: ₦{Number(kycRequirements.spendableBalance || 0).toLocaleString()}).
              </p>
              <button
                type="button"
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors cursor-pointer border-0"
                onClick={() =>
                  navigate(AppRoutes.wallet || "/wallet", {
                    state: { openFundWallet: true },
                  })
                }
              >
                Fund Wallet (₦{Number(kycRequirements.shortfall || 0).toLocaleString()} needed)
              </button>
            </div>
          )}

          <div className="welcome-buttons mt-8">
            <div className="welcome-buttons-content">
              {!loading ? (
                <button
                  className={`btn-primary bg-black text-white w-full py-4 px-8 rounded-xl font-semibold transition-colors ${kycRequirements && kycRequirements.hasEnoughBalance === false
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-800 cursor-pointer"
                    }`}
                  type="submit"
                  disabled={kycRequirements && kycRequirements.hasEnoughBalance === false}
                >
                  Verify BVN & Upgrade
                </button>
              ) : (
                <button
                  className="btn-primary bg-black text-white w-full py-3 px-8 rounded-lg font-semibold opacity-60"
                  disabled
                >
                  <Spin indicator={antIcon} />
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 ">
            <button
              className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-all duration-300"
              onClick={() => setVerificationStep("instructions")}
              disabled={loading}
              aria-label="Go back to instructions"
            >
              ← Back to Instructions
            </button>
          </div>
        </Form>
      </div>
    </div>
  );

  const renderVerifying = () => (
    <div className="contain p-4 md:p-8 bg-gradient-to-br from-blue-50 via-white to-indigo-50 min-h-screen flex items-center justify-center">
      <div className=" py-12 md:py-16 bg-white rounded-2xl p-6 md:p-10 shadow-2xl border border-gray-100 max-w-lg w-full transition-all duration-700 ease-in-out transform hover:shadow-3xl">
        <div className="mb-8 inline-block p-6 bg-black rounded-full shadow-xl">
          <Spin indicator={newAntIcon} />
        </div>

        <Progress
          percent={verificationProgress}
          showInfo={false}
          strokeColor={{
            "0%": "#3b82f6",
            "100%": "#6366f1",
          }}
          className="mb-8 h-3 rounded-full"
        />

        <div className="space-y-4 mb-8">
          {verificationSteps.map((step, index) => (
            <div
              key={step.key}
              className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-500 ease-in-out transform ${index <= currentVerificationStep
                ? "bg-black shadow-md"
                : "bg-gray-50 border border-gray-200"
                } ${index === currentVerificationStep ? "scale-105" : ""}`}
            >
              <span className="text-2xl animate-pulse">{step.icon}</span>
              <span
                className={`text-xl font-medium ${index <= currentVerificationStep
                  ? "text-blue-800"
                  : "text-gray-500"
                  }`}
              >
                {step.label}
              </span>
              {index < currentVerificationStep && (
                <CheckCircleOutlined className="text-green-500 ml-auto text-xl" />
              )}
            </div>
          ))}
        </div>

        <h4 className="sec-page-title text-2xl md:text-xl font-bold text-gray-900 mb-4">
          Verifying Your BVN
        </h4>
        <p className="text-gray-600 leading-relaxed mb-4">
          {verificationMessage}
        </p>
        <p className="text-xs text-gray-500">
          This may take up to 5 minutes. Please do not close this page.
        </p>
      </div>
    </div>
  );

  return (
    <div>
      <Spin spinning={loading} indicator={newAntIcon}>
        <div className="profile-display settings-display form-page">
          <TopNav title="BVN Verification" />
          <div className="display-main-content white-b">
            <div className="">
              <div>
                {verificationStep === "instructions" && renderInstructions()}
                {verificationStep === "form" && renderForm()}
                {verificationStep === "verifying" && renderVerifying()}
              </div>
            </div>
          </div>
        </div>
      </Spin>

      <Modal
        title={
          <div className="flex items-center gap-2 font-gilroyBold">
            <ClockCircleOutlined className="text-orange-500" />
            <span>Verification Timeout</span>
          </div>
        }
        open={timeoutModalVisible}
        onCancel={() => setTimeoutModalVisible(false)}
        footer={[
          <Button
            key="retry"
            className="font-semibold px-6 py-2 rounded-lg transition-all duration-300 mr-2"
            onClick={() => {
              setTimeoutModalVisible(false);
              setVerificationStep("form");
            }}
          >
            Try Again
          </Button>,
          <Button
            key="cancel"
            className="bg-gray-200 hover:bg-gray-300 border-none text-gray-700 font-semibold px-6 py-2 rounded-lg transition-all duration-300"
            onClick={() => {
              setTimeoutModalVisible(false);
              navigate("/wallet");
            }}
          >
            Go Back to Wallet
          </Button>,
        ]}
        centered
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-gray-800 font-medium">
              Your BVN verification is taking longer than expected. This may be
              due to network issues or high server load.
            </p>
          </div>
          <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
            <p className="text-[13px] text-orange-800 font-semibold mb-3">
              What you can do:
            </p>
            <ul className="text-base text-orange-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                <span>Try again in a few minutes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                <span>Check your internet connection</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">•</span>
                <span>Ensure your BVN details are correct</span>
              </li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BVNVerification;
